import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Create / edit a Sales Contract. Mirrors RfqFormPage.tsx's structure:
// header fields plus a dynamic item-rows table of contracted rates. Editing
// is only legal while the contract is DRAFT.
// ---------------------------------------------------------------------------

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];

interface Customer {
  id: number | string;
  customer_code?: string;
  company_name: string;
}

interface Product {
  id: string | number;
  product_name: string;
  hsn_code?: string;
  unit?: string;
  is_active?: boolean;
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  product: Product | null;
  description: string;
  contractRate: string;
  minQuantity: string;
  maxQuantity: string;
  uom: string;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  description: '',
  contractRate: '',
  minQuantity: '0',
  maxQuantity: '',
  uom: '',
});

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

interface ContractItemFromApi {
  product_id: string | number | null;
  product_name?: string | null;
  contract_rate: number | string;
  min_quantity: number | string | null;
  max_quantity: number | string | null;
  uom: string | null;
}

interface ContractFromApi {
  id: number;
  customer_id: string | number;
  contract_date: string;
  valid_from: string | null;
  valid_to: string | null;
  payment_terms: string | null;
  credit_days: number | string | null;
  delivery_terms: string | null;
  price_escalation: string | null;
  currency: string | null;
  notes: string | null;
  status: string;
  items: ContractItemFromApi[];
}

const SalesContractFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingContract, setLoadingContract] = useState(isEdit);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [contractDate, setContractDate] = useState(todayISO());
  const [validFrom, setValidFrom] = useState(todayISO());
  const [validTo, setValidTo] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [creditDays, setCreditDays] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [priceEscalation, setPriceEscalation] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      api.get<{ customers: Customer[] }>('/sales/customers').catch(() => ({ customers: [] as Customer[] })),
      api.get<{ products: Product[] }>('/products').catch(() => ({ products: [] as Product[] })),
    ])
      .then(([customersRes, productsRes]) => {
        setCustomers(customersRes.customers || []);
        setProducts((productsRes.products || []).filter((p) => p.is_active !== false));
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingContract(true);
    api
      .get<{ contract: ContractFromApi }>(`/sales-contracts/${id}`)
      .then(({ contract }) => {
        setCustomer(customers.find((c) => String(c.id) === String(contract.customer_id)) || null);
        setContractDate(contract.contract_date ? contract.contract_date.slice(0, 10) : todayISO());
        setValidFrom(contract.valid_from ? contract.valid_from.slice(0, 10) : todayISO());
        setValidTo(contract.valid_to ? contract.valid_to.slice(0, 10) : '');
        setPaymentTerms(contract.payment_terms || '');
        setCreditDays(contract.credit_days !== null && contract.credit_days !== undefined ? String(contract.credit_days) : '');
        setDeliveryTerms(contract.delivery_terms || '');
        setPriceEscalation(contract.price_escalation || '');
        setCurrency(contract.currency || 'INR');
        setNotes(contract.notes || '');
        setStatus(contract.status);
        setItems(
          (contract.items || []).map((it) => ({
            key: nextDraftKey(),
            product: products.find((p) => String(p.id) === String(it.product_id)) || null,
            description: it.product_name || '',
            contractRate: String(toNum(it.contract_rate)),
            minQuantity: it.min_quantity !== null && it.min_quantity !== undefined ? String(toNum(it.min_quantity)) : '0',
            maxQuantity: it.max_quantity !== null && it.max_quantity !== undefined ? String(toNum(it.max_quantity)) : '',
            uom: it.uom || '',
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load contract'))
      .finally(() => setLoadingContract(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          product,
          uom: it.uom || product?.unit || '',
          description: it.description || product?.product_name || '',
        };
      })
    );
  };

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!customer) errs.push('Select the customer this contract is with.');
    if (!contractDate) errs.push('Contract date is required.');
    if (!validFrom) errs.push('Valid-from date is required.');
    if (items.length === 0) errs.push('At least one line item is required.');
    items.forEach((it, idx) => {
      if (toNum(it.contractRate) <= 0) errs.push(`Line ${idx + 1}: contract rate must be greater than 0.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        customerId: customer!.id,
        contractDate,
        validFrom,
        validTo: validTo || undefined,
        paymentTerms: paymentTerms.trim() || undefined,
        creditDays: creditDays ? toNum(creditDays) : undefined,
        deliveryTerms: deliveryTerms.trim() || undefined,
        priceEscalation: priceEscalation.trim() || undefined,
        currency: currency || 'INR',
        notes: notes.trim() || undefined,
        items: items.map((it) => ({
          productId: it.product?.id || undefined,
          description: it.description.trim() || it.product?.product_name || undefined,
          contractRate: toNum(it.contractRate),
          minQuantity: it.minQuantity ? toNum(it.minQuantity) : 0,
          maxQuantity: it.maxQuantity ? toNum(it.maxQuantity) : undefined,
          uom: it.uom.trim() || undefined,
        })),
      };

      const response =
        isEdit && id
          ? await api.put<{ contract: { id: number } }>(`/sales-contracts/${id}`, payload)
          : await api.post<{ contract: { id: number } }>('/sales-contracts', payload);

      navigate(`/sales/contracts/${response.contract.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save contract');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingContract;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(isEdit && id ? `/sales/contracts/${id}` : '/sales/contracts')}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Sales Contract' : 'New Sales Contract'}
        subtitle="Lock in rates a customer can order against, over a validity window."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This contract is {status} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Contract Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              size="small"
              options={customers}
              value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit}
              renderInput={(params) => <TextField {...params} label="Customer *" placeholder="Select customer" />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Contract Date"
              value={contractDate}
              onChange={(e) => setContractDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={!canEdit}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid From"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Valid To"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. Net 30"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Credit Days"
              value={creditDays}
              onChange={(e) => setCreditDays(e.target.value)}
              inputProps={{ min: 0 }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Delivery Terms"
              value={deliveryTerms}
              onChange={(e) => setDeliveryTerms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. FOB, Ex-works"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Price Escalation Clause"
              value={priceEscalation}
              onChange={(e) => setPriceEscalation(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Contracted Rates
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addItem}>
              Add Item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Product</TableCell>
                <TableCell sx={headerCellSx} align="right">Contract Rate</TableCell>
                <TableCell sx={headerCellSx} align="right">Min Qty</TableCell>
                <TableCell sx={headerCellSx} align="right">Max Qty</TableCell>
                <TableCell sx={headerCellSx}>UOM</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Autocomplete
                      size="small"
                      options={products}
                      value={it.product}
                      onChange={(_, v) => handleProductChange(it.key, v)}
                      getOptionLabel={(o) => `${o.product_name}${o.hsn_code ? ` (${o.hsn_code})` : ''}`}
                      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                      disabled={!canEdit}
                      renderInput={(params) => <TextField {...params} placeholder="Select product" />}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.contractRate}
                      onChange={(e) => updateItem(it.key, { contractRate: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.minQuantity}
                      onChange={(e) => updateItem(it.key, { minQuantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.maxQuantity}
                      onChange={(e) => updateItem(it.key, { maxQuantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={it.uom}
                      onChange={(e) => updateItem(it.key, { uom: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeItem(it.key)} disabled={items.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={() => navigate(isEdit && id ? `/sales/contracts/${id}` : '/sales/contracts')}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Contract'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SalesContractFormPage;
